import { EquipmentType, dollars, dollarsToNum } from "."

export type Adder = {
  description: string
  amount: number
  notes?: string
}

// amount is converted to string for usage in forms
export type FormAdder = {
  description: string
  amount: string
  notes?: string
}

export enum AdderTypes {
  RoofPrice = 'Roof Price',
  StoragePrice = 'Storage Price',
  TreeRemovalTrimmingsPrice = 'Tree Removal or Trimmings Price',
  HvacPrice = 'HVAC Price',
  Downpayment = 'Downpayment',
  Other = 'Other',
  RebatesAndOrCredits = 'Incentives/Rebates',
}

const CreditAddersTypes = [AdderTypes.Downpayment, AdderTypes.RebatesAndOrCredits]

export class Adders {
  private adders: Adder[]

  constructor(adders: Adder[] | FormAdder[]) {
    this.adders = adders?.map(a => {
      // Handle both Mongoose documents and plain objects
      const amount = typeof a.amount === 'number' ? a.amount : dollarsToNum(a.amount)
      return {
        description: a.description,
        amount: Math.abs(amount),
        notes: a.notes
      }
    }).sort((a, b) => {
      return a.description.localeCompare(b.description)
    }) || []
  }

  public getList(): Adder[] {
    return this.adders
  }

  static areValid(input: any): boolean {
    if (!Array.isArray(input)) {
      return false
    }

    const isValidAdder = adder => (
      typeof adder.description === "string" &&
      typeof adder.amount === "number"
    )

    return input.every(isValidAdder)
  }

  static isCreditAdder(type: AdderTypes): boolean {
    return CreditAddersTypes.includes(type)
  }

  setCostFor(type: AdderTypes, amount: number): void {
    const adder = this.adders.find(a => a.description === type)
    if (adder) {
      adder.amount = amount
    } else {
      this.adders.push({
        description: type,
        amount
      })
    }
  }

  costFor(type: AdderTypes): number {
    return this.adders.find(a => a.description === type)?.amount || 0
  }

  for(type: AdderTypes): Adder | undefined {
    return this.adders.find(a => a.description === type)
  }

  getTotal(): number {
    return this.adders.reduce((acc, adder) => {
      if (CreditAddersTypes.includes(adder.description as AdderTypes)) {
        return acc - adder.amount
      }
      return acc + adder.amount
    }, 0)
  }

  getCreditAddersTotal(): number {
    return this.adders
      .filter(adder => CreditAddersTypes.includes(adder.description as AdderTypes))
      .reduce((total, adder) => total + adder.amount, 0)
  }

  // excludes downpayment and incentives/rebates
  getTotalCosts(): number {
    return this.adders
      .filter(a => !CreditAddersTypes.includes(a.description as AdderTypes))
      .reduce((acc, adder) => {
        return acc + adder.amount
      }, 0)
  }

  getBreakdown(): string {
    const sortedAdders = this.adders.slice().sort((a, b) => {
      return CreditAddersTypes.includes(a.description as AdderTypes) ? 1 : -1
    })

    return sortedAdders.map(a => {
      let descr = a.description
      if (descr === AdderTypes.Other) {
        descr = `${descr}*`
      }
      if (CreditAddersTypes.includes(a.description as AdderTypes)) {
        return `${descr}: -${dollars(a.amount)}`
      }
      return `${descr}: ${dollars(a.amount)}`
    }).join('\n')
  }

  getRebatesAndOrCreditsAmount(): number {
    return this.adders?.find(a => a.description === AdderTypes.RebatesAndOrCredits)?.amount || 0
  }

  getDownpaymentAmount(): number {
    return this.adders.find(a => a.description === AdderTypes.Downpayment)?.amount || 0
  }

  getStoragePrice(): number {
    return this.adders.find(a => a.description === AdderTypes.StoragePrice)?.amount || 0
  }

  getRoofPrice(): number {
    return this.adders.find(a => a.description === AdderTypes.RoofPrice)?.amount || 0
  }

  upsertList(list: Adder[]): Adders {
    for (const adder of list) {
      const existing = this.adders.find(a => a.description === adder.description)
      if (existing) {
        existing.amount = adder.amount
      } else {
        this.adders.push(adder)
      }
    }

    return this
  }

  getRebatesAndOrCreditsAdder(): Adder | undefined {
    return this.adders?.find(a => a.description === AdderTypes.RebatesAndOrCredits)
  }

  getNonCreditAdders(equipmentType: EquipmentType): FormAdder[] {
    return this.adders
      ?.filter(adder => !CreditAddersTypes.includes(adder.description as AdderTypes))
      ?.map(adder => ({
        description: adder.description === AdderTypes.StoragePrice &&
          [EquipmentType.TeslaRoof, EquipmentType.TeslaSolar].includes(equipmentType)
          ? 'Powerwall Total Price'
          : adder.description,
        amount: dollars(adder.amount)
      }))
  }

  getCustomAdders(): Adder[] {
    const standardTypes = Object.values(AdderTypes)
    return this.adders.filter(adder => !standardTypes.includes(adder.description as AdderTypes))
  }

  filterNonZero(): Adders {
    this.adders = this.adders.filter(a => a.amount !== 0)
    return this
  }
}